<script setup>
import TrackingPositionDetailsCell from "@frontJs/components/tables/cells/projectManagement/seo/TrackingPositionDetailsCell.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  baseRequest: {
    type: Object,
    required: false,
    default: () => [],
  },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  data,
  count,
  pagination,
  pageCount,
  sorting,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.name,
          href: route("front.projectManagement.seo.positionTracking", {
            tenant: props.row.original.tenant.id,
          }),
          inertia: true,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "tracked_keywords",
      header: () => t("all.text.Tracked keywords"),
      cell: (props) =>
        h(TextCellV2, {
          text: String(props.row.original.tracked_keywords),
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "positioned_keywords",
      header: () => t("all.text.Positioned keywords"),
      cell: (props) =>
        h(TextCellV2, {
          text: String(props.row.original.positioned_keywords),
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "positioned_urls",
      header: () => t("all.text.Positioned URLs"),
      cell: (props) =>
        h(TextCellV2, {
          text: String(props.row.original.positioned_urls),
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "won_positions",
      header: () => t("all.text.Positions won"),
      cell: (props) =>
        h(TextCellV2, {
          text: String(props.row.original.won_positions),
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "lost_positions",
      header: () => t("all.text.Positions lost"),
      cell: (props) =>
        h(TextCellV2, {
          text: String(props.row.original.lost_positions),
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "won_entries",
      header: () => t("all.text.Entries"),
      cell: (props) =>
        h(TextCellV2, {
          text: String(props.row.original.won_entries),
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "lost_entries",
      header: () => t("all.text.Exits"),
      cell: (props) =>
        h(TextCellV2, {
          text: String(props.row.original.lost_entries),
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "last_update",
      header: () => t("all.text.Last update"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.last_update,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "actions",
      header: () => t("label.actions"),
      cell: (props) =>
        h(TrackingPositionDetailsCell, {
          row: props.row ?? "-",
          tenantId: props.row.original.tenant?.id,
        }),
      enableSorting: false,
    }),
  ],
  updateData,
  baseRequest: props.baseRequest,
  columnPinning: {
    right: ["actions"],
  },
});

sorting.value = [
  {
    id: "name",
    desc: false,
  },
];

function updateData() {
  loading.value = true;

  let url = route("front.projectManagement.seo.postPositionTracking");

  let postData = {
    pageIndex: pagination.value.pageIndex,
    pageSize: pagination.value.pageSize,
    sorting: sorting.value,
    globalFilter: globalFilter.value,
    filters: filters.value,
  };

  let config = {};

  axios
    .post(url, postData, config)
    .then((response) => {
      data.value = response.data.data;
      count.value = response.data.count;
      pagination.value.pageIndex = response.data.pageIndex;
      pagination.value.pageSize = response.data.pageSize;
      pageCount.value = response.data.pageCount;
    })
    .catch((error) => {
      console.log(error);
    })
    .finally(() => {
      loading.value = false;
    });
}
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <div v-else>
    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :set-global-filter="setGlobalFilter"
      :available-filters="availableFilters"
      :filters="filters"
      :set-filters="setFilters"
    />
  </div>
</template>
